(() => {
  if (customElements.get('noukki-product-videos')) return;
  const choices = new Map();
  class NoukkiProductVideos extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-nkv-track]');
      if (!this.track) return;
      this.controller = new AbortController();
      this.signal = this.controller.signal;
      this.cards = [...this.querySelectorAll('[data-nkv-card]')];
      this.dialog = this.querySelector('[data-nkv-dialog]');
      this.player = this.querySelector('[data-nkv-player]');
      this.toggle = this.querySelector('[data-nkv-toggle]');
      this.sound = this.querySelector('[data-nkv-sound]');
      this.retry = this.querySelector('[data-nkv-retry]');
      this.message = this.querySelector('[data-nkv-message]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.connection = navigator.connection;
      this.choice = choices.get(this.dataset.sectionId);
      this.ratios = new Map();
      this.nearCards = new Set();
      this.pending = new WeakSet();
      this.blocked = new WeakSet();
      this.near = false;
      this.on(this, 'click', event => {
        const open = event.target.closest('[data-nkv-open]');
        if (open) this.open(open);
        if (event.target.closest('[data-nkv-toggle]')) {
          this.choice = this.canPlay() ? 'pause' : 'play';
          choices.set(this.dataset.sectionId, this.choice);
          this.blocked = new WeakSet();
          this.sync();
        }
        const step = event.target.closest('[data-nkv-step]');
        if (step) this.track.scrollBy({left: Number(step.dataset.nkvStep) * this.track.clientWidth, behavior:this.motion.matches ? 'instant' : 'smooth'});
      });
      this.on(this.querySelector('[data-nkv-close]'), 'click', () => this.close());
      this.on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      this.on(this.dialog, 'close', () => this.finishClose());
      this.on(this.dialog, 'pointerdown', event => { this.backdropDown = event.target === this.dialog; });
      this.on(this.dialog, 'click', event => { if (event.target === this.dialog && this.backdropDown) this.close(); });
      this.on(this.dialog, 'keydown', event => this.trapFocus(event));
      this.on(this.sound, 'click', () => { this.player.muted = !this.player.muted; this.updateSound(); });
      this.on(this.player, 'volumechange', () => this.updateSound());
      this.on(this.retry, 'click', () => this.playFull());
      this.on(this.player, 'playing', () => { this.retry.hidden = true; this.message.hidden = true; });
      this.on(this.player, 'error', () => { if (this.dialog.open) { this.retry.hidden = false; this.message.hidden = false; } });
      this.on(document, 'visibilitychange', () => {
        if (document.hidden) this.player.pause();
        this.sync();
      });
      this.on(this.motion, 'change', () => this.sync());
      if (this.connection) this.on(this.connection, 'change', () => this.sync());
      this.on(this.track, 'scroll', () => this.schedule(), {passive:true});
      this.on(document, 'shopify:block:select', event => {
        const card = this.cards.find(c => c.dataset.blockId === event.detail.blockId);
        if (card) card.scrollIntoView({block:'nearest',inline:'nearest',behavior:'instant'});
      });
      this.nearObserver = new IntersectionObserver(entries => {
        this.near = entries[0].isIntersecting;
        this.sync();
      }, {rootMargin:'240px 0px'});
      this.nearObserver.observe(this);
      this.visibilityObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => this.ratios.set(entry.target, entry.intersectionRatio));
        this.sync();
      }, {threshold:[0, .25, .5, .75, 1]});
      this.horizontalObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? this.nearCards.add(entry.target) : this.nearCards.delete(entry.target));
        this.sync();
      }, {root:this.track, rootMargin:'0px 60px'});
      this.cards.forEach(card => {
        const video = card.querySelector('[data-nkv-preview]');
        video.muted = true;
        video.defaultMuted = true;
        this.on(video, 'playing', () => {
          if (!this.allowed(card)) { video.pause(); return; }
          card.dataset.playing = '';
        });
        this.on(video, 'error', () => { this.blocked.add(card); delete card.dataset.playing; });
        this.visibilityObserver.observe(card);
        this.horizontalObserver.observe(card);
      });
      this.resizeObserver = new ResizeObserver(() => this.schedule());
      this.resizeObserver.observe(this.track);
      this.overlayObserver = new MutationObserver(() => this.schedule());
      this.overlayObserver.observe(document.body, {subtree:true,attributes:true,attributeFilter:['open','aria-hidden']});
      this.sync();
    }
    on(target, type, handler, options = {}) {
      target.addEventListener(type, handler, {...options,signal:this.signal});
    }
    canPlay() {
      if (this.choice === 'pause') return false;
      if (this.choice === 'play') return true;
      return this.dataset.autoplay === 'true' && !this.motion.matches && !this.connection?.saveData;
    }
    otherOverlay() {
      return [...document.querySelectorAll('dialog[open],theme-drawer[open],[role="dialog"][aria-modal="true"]')].some(el => el !== this.dialog && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
    }
    allowed(card) {
      return this.isConnected && this.canPlay() && !document.hidden && !this.dialog.open && !this.otherOverlay() && (this.ratios.get(card) || 0) >= .5;
    }
    sources(card, kind) {
      try {
        const sources = JSON.parse(card.dataset[kind + 'Sources'] || '[]');
        return Array.isArray(sources) ? sources.filter(source => typeof source.url === 'string' && source.url.trim()) : [];
      } catch { return []; }
    }
    choose(card, kind) {
      const sources = this.sources(card, kind);
      const mp4 = sources.filter(s => s.format === 'mp4' || s.mime_type === 'video/mp4').sort((a,b) => a.width - b.width);
      const target = kind === 'preview' ? Math.min(480, Math.ceil(card.clientWidth * Math.min(devicePixelRatio || 1, 2))) : Math.min(1080, Math.ceil(Math.min(innerWidth - 32, 780) * Math.min(devicePixelRatio || 1, 2)));
      if (mp4.length) return mp4.find(s => s.width >= target) || mp4.at(-1);
      return sources.find(s => this.player.canPlayType(s.mime_type || 'application/vnd.apple.mpegurl'));
    }
    loadPreview(card) {
      const video = card.querySelector('[data-nkv-preview]');
      if (video.hasAttribute('src')) return video;
      const source = this.choose(card, 'preview');
      if (source) { video.src = source.url; video.preload = 'metadata'; }
      return video;
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = 0; if (this.isConnected) this.sync(); });
    }
    sync() {
      if (!this.controller || !this.isConnected) return;
      const enabled = this.canPlay();
      this.toggle.setAttribute('aria-pressed', String(!enabled));
      this.toggle.setAttribute('aria-label', enabled ? this.dataset.pauseLabel : this.dataset.playLabel);
      const suspended = document.hidden || this.dialog.open || this.otherOverlay();
      const visible = this.cards.filter(card => (this.ratios.get(card) || 0) >= .5).sort((a,b) => (this.ratios.get(b)||0) - (this.ratios.get(a)||0)).slice(0,3);
      this.cards.forEach(card => {
        let video = card.querySelector('[data-nkv-preview]');
        if (enabled && !suspended && this.near && this.nearCards.has(card)) video = this.loadPreview(card);
        if (!enabled || suspended || !visible.includes(card) || this.blocked.has(card)) { video.pause(); return; }
        if (!video.hasAttribute('src') || !video.paused || this.pending.has(video)) return;
        video.muted = true;
        this.pending.add(video);
        const play = video.play();
        if (play) play.then(() => { if (!this.allowed(card)) video.pause(); }).catch(error => {
          if (error.name !== 'AbortError') { this.blocked.add(card); delete card.dataset.playing; }
        }).finally(() => {
          this.pending.delete(video);
          if (this.allowed(card) && video.paused && !this.blocked.has(card)) this.schedule();
        });
        else this.pending.delete(video);
      });
      const overflowing = this.track.scrollWidth > this.track.clientWidth + 8;
      this.querySelector('[data-nkv-nav]').hidden = !overflowing;
      this.querySelector('[data-nkv-step="-1"]').disabled = this.track.scrollLeft < 2;
      this.querySelector('[data-nkv-step="1"]').disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
    }
    open(button) {
      if (this.dialog.open || this.otherOverlay()) return;
      const card = button.closest('[data-nkv-card]');
      const source = this.choose(card, 'full');
      this.returnFocus = button;
      this.scroll = {x:scrollX,y:scrollY,overflow:document.documentElement.style.overflow};
      this.player.pause();
      this.player.defaultMuted = false;
      this.player.muted = false;
      this.player.volume = 1;
      this.player.poster = card.dataset.poster || '';
      this.player.setAttribute('aria-label', card.dataset.label);
      this.dialog.setAttribute('aria-label', card.dataset.label);
      this.player.style.setProperty('--nkv-player-ratio', Number(card.dataset.aspect) > 0 ? card.dataset.aspect : '0.5625');
      this.player.style.width = `min(100%, ${Math.min(780, 680 * (Number(card.dataset.aspect) || .5625))}px)`;
      if (source) {
        if (this.player.getAttribute('src') !== source.url) this.player.src = source.url;
        this.player.currentTime = 0;
      }
      this.retry.hidden = true;
      this.message.hidden = true;
      this.dialog.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.sync();
      this.updateSound();
      this.querySelector('[data-nkv-close]').focus({preventScroll:true});
      if (source) this.playFull();
      else { this.retry.hidden = false; this.message.hidden = false; }
    }
    playFull() {
      if (!this.dialog.open) return;
      const play = this.player.play();
      if (play) play.then(() => { if (!this.dialog.open || !this.isConnected || document.hidden) this.player.pause(); }).catch(error => {
        if (this.dialog.open && error.name !== 'AbortError') { this.retry.hidden = false; this.message.hidden = false; }
      });
    }
    updateSound() {
      this.sound.textContent = this.player.muted ? this.dataset.unmuteLabel : this.dataset.muteLabel;
      this.sound.setAttribute('aria-pressed', String(!this.player.muted));
    }
    close() {
      if (this.dialog.open) this.dialog.close();
      this.finishClose();
    }
    finishClose() {
      if (!this.scroll) return;
      this.player.pause();
      this.player.muted = true;
      this.player.removeAttribute('src');
      this.player.load();
      document.documentElement.style.overflow = this.scroll.overflow;
      window.scrollTo({left:this.scroll.x,top:this.scroll.y,behavior:'instant'});
      this.scroll = null;
      if (this.returnFocus?.isConnected) this.returnFocus.focus({preventScroll:true});
      this.sync();
    }
    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const focusable = [...this.dialog.querySelectorAll('button:not([disabled]),video[controls]')].filter(el => !el.hidden && el.getClientRects().length);
      const first = focusable[0], last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    disconnectedCallback() {
      if (!this.controller) return;
      this.close();
      this.controller.abort();
      this.controller = null;
      [this.nearObserver,this.visibilityObserver,this.horizontalObserver,this.resizeObserver,this.overlayObserver].forEach(observer => observer?.disconnect());
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.cards.forEach(card => { const video = card.querySelector('video'); video.pause(); video.removeAttribute('src'); video.load(); delete card.dataset.playing; });
    }
  }
  customElements.define('noukki-product-videos', NoukkiProductVideos);
})();
